import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import dayjs from "dayjs";
import { MapPin, Building2, Wallet, ArrowLeft, Loader2, Send, CheckCircle2, Globe } from "lucide-react";
import Navbar from "../components/Navbar";
import { useLanguage } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";

export default function JobDetail() {
  const { jobId } = useParams();
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [applied, setApplied] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [message, setMessage] = useState("");
  const [expYears, setExpYears] = useState("");
  const [answers, setAnswers] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [expDetail, setExpDetail] = useState("");
  const [quals, setQuals] = useState("");
  const [sending, setSending] = useState(false);
  const EXP_OPTIONS = ["lt1", "1-3", "3-5", "5+"];
  const locale = lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-US";

  useEffect(() => {
    api.get(`/jobs/${jobId}`).then(({ data }) => setJob(data)).catch(() => navigate("/jobs"));
  }, [jobId, navigate]);

  useEffect(() => {
    if (user?.role === "candidate") {
      api.get(`/applications/check/${jobId}`).then(({ data }) => setApplied(data.applied)).catch(() => {});
    }
  }, [user, jobId]);

  const handleApply = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/applications", {
        job_id: jobId, message, experience_years: expYears, answers,
        first_name: firstName, last_name: lastName, birthdate,
        experience_detail: expDetail, qualifications: quals,
      });
      setApplied(true);
      setShowApply(false);
      toast.success(t("jobs.applicationSent"));
    } catch (err) {
      toast.error(formatApiError(err, t("common.error")));
    } finally {
      setSending(false);
    }
  };

  if (!job) {
    return (
      <div className="min-h-screen bg-slate-50"><Navbar />
        <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>
      </div>
    );
  }

  const isActive = job.status === "active";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="job-detail-page">
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <Link to="/jobs" data-testid="back-to-jobs" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-sky-600">
          <ArrowLeft className="h-4 w-4" strokeWidth={2} /> {t("common.back")}
        </Link>

        <div className="mt-6 grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="rounded-md border-2 border-slate-900 bg-white p-8 shadow-[6px_6px_0px_0px_rgba(56,189,248,1)]">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full border border-slate-900 bg-sky-100 px-3 py-0.5 text-xs font-semibold text-slate-900">{t(`jobTypes.${job.job_type}`)}</span>
                <span className="rounded-full border border-slate-900 bg-yellow-100 px-3 py-0.5 text-xs font-semibold text-slate-900">{t(`categories.${job.category}`)}</span>
                {!isActive && <span className="rounded-full border border-slate-900 bg-slate-100 px-3 py-0.5 text-xs font-semibold text-slate-600">{t("status.inactive")}</span>}
              </div>
              <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-slate-900 md:text-4xl" data-testid="job-title">{job.title}</h1>
              <p className="mt-2 flex items-center gap-2 text-base font-medium text-slate-600">
                <Building2 className="h-5 w-5" strokeWidth={2} /> {job.company_name}
              </p>
              <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-slate-200 pt-4 text-sm text-slate-600">
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" strokeWidth={2} />{job.location}</span>
                {job.salary && <span className="flex items-center gap-1.5"><Wallet className="h-4 w-4" strokeWidth={2} />{job.salary}</span>}
                <span className="text-slate-500">{t("jobs.postedOn")} {dayjs(job.created_at).locale(locale).format("DD MMM YYYY")}</span>
              </div>

              {(job.requirements || job.min_experience) && (
                <div className="mt-6 rounded-md border-2 border-slate-900 bg-sky-50 p-5" data-testid="job-requirements">
                  {job.min_experience && (
                    <p className="text-sm"><span className="font-bold text-slate-900">{t("jobs.minExpLabel")}:</span> <span className="text-slate-700">{t(`experience.${job.min_experience}`)}</span></p>
                  )}
                  {job.requirements && (
                    <div className={job.min_experience ? "mt-3" : ""}>
                      <p className="text-sm font-bold text-slate-900">{t("jobs.requirementsLabel")}:</p>
                      <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{job.requirements}</p>
                    </div>
                  )}
                </div>
              )}

              <h2 className="mt-8 font-heading text-xl font-bold text-slate-900">{t("jobs.aboutJob")}</h2>
              <div className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-700" data-testid="job-description">{job.description}</div>
            </div>
          </div>

          <div>
            <div className="rounded-md border-2 border-slate-900 bg-white p-6 lg:sticky lg:top-24">
              {!isActive ? (
                <p className="text-center text-sm font-semibold text-slate-500">{t("jobs.inactive")}</p>
              ) : applied ? (
                <div className="flex items-center justify-center gap-2 rounded-full border-2 border-slate-900 bg-green-100 py-3 text-sm font-bold text-green-900" data-testid="applied-indicator">
                  <CheckCircle2 className="h-5 w-5" strokeWidth={2} /> {t("jobs.applied")}
                </div>
              ) : user?.role === "candidate" ? (
                <button
                  onClick={() => {
                    setAnswers(new Array((job.questions || []).length).fill(""));
                    const parts = (user?.name || "").trim().split(/\s+/);
                    setFirstName(parts[0] || "");
                    setLastName(parts.slice(1).join(" ") || "");
                    setShowApply(true);
                  }}
                  data-testid="job-apply-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 py-3 font-heading text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]"
                >
                  <Send className="h-4 w-4" strokeWidth={2.5} /> {t("jobs.apply")}
                </button>
              ) : !user ? (
                <button
                  onClick={() => navigate("/login")}
                  data-testid="job-login-to-apply"
                  className="w-full rounded-full border-2 border-slate-900 bg-sky-400 py-3 font-heading text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]"
                >
                  {t("jobs.loginToApply")}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* Apply modal */}
      {showApply && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-sm" data-testid="apply-modal">
          <div className="w-full max-w-lg rounded-md border-2 border-slate-900 bg-white p-8 shadow-[8px_8px_0px_0px_rgba(15,23,42,1)] max-h-[90vh] overflow-y-auto" data-testid="apply-modal-card">
            <h2 className="font-heading text-xl font-bold text-slate-900">{t("jobs.applyTitle")}</h2>
            <p className="mt-1 text-sm text-slate-500">{job.title} — {job.company_name}</p>
            <form onSubmit={handleApply} className="mt-5 space-y-4" data-testid="apply-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("jobs.firstName")}</label>
                  <input
                    value={firstName} onChange={(e) => setFirstName(e.target.value)} required
                    data-testid="apply-first-name"
                    className="mt-1.5 w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("jobs.lastName")}</label>
                  <input
                    value={lastName} onChange={(e) => setLastName(e.target.value)} required
                    data-testid="apply-last-name"
                    className="mt-1.5 w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("jobs.birthdate")}</label>
                <input
                  type="date" value={birthdate} onChange={(e) => setBirthdate(e.target.value)} required
                  data-testid="apply-birthdate"
                  className="mt-1.5 w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("jobs.yourExperience")}</label>
                <select
                  value={expYears} onChange={(e) => setExpYears(e.target.value)} required
                  data-testid="apply-experience-select"
                  className="mt-1.5 w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                >
                  <option value="">{t("jobs.selectExperience")}</option>
                  {EXP_OPTIONS.map((x) => <option key={x} value={x}>{t(`experience.${x}`)}</option>)}
                </select>
              </div>

              {(job.questions || []).length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("jobs.recruiterQuestions")}</p>
                  {job.questions.map((q, i) => (
                    <div key={i}>
                      <label className="text-sm font-semibold text-slate-800">{i + 1}. {q.text}</label>
                      {q.type === "yesno" ? (
                        <select
                          value={answers[i] || ""} required
                          onChange={(e) => { const a = [...answers]; a[i] = e.target.value; setAnswers(a); }}
                          data-testid={`apply-answer-${i}`}
                          className="mt-1 w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                        >
                          <option value="">—</option>
                          <option value="yes">{t("common.yes")}</option>
                          <option value="no">{t("common.no")}</option>
                        </select>
                      ) : (
                        <textarea
                          value={answers[i] || ""} required rows={2}
                          onChange={(e) => { const a = [...answers]; a[i] = e.target.value; setAnswers(a); }}
                          placeholder={t("jobs.answerPlaceholder")}
                          data-testid={`apply-answer-${i}`}
                          className="mt-1 w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("jobs.proExperience")}</label>
                <textarea
                  value={expDetail} onChange={(e) => setExpDetail(e.target.value)} required rows={3}
                  placeholder={t("jobs.proExperiencePlaceholder")}
                  data-testid="apply-experience-detail"
                  className="mt-1.5 w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("jobs.qualificationsLabel")}</label>
                <textarea
                  value={quals} onChange={(e) => setQuals(e.target.value)} required rows={3}
                  placeholder={t("jobs.qualificationsPlaceholder")}
                  data-testid="apply-qualifications"
                  className="mt-1.5 w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("jobs.applyMessage")}</label>
                <textarea
                  value={message} onChange={(e) => setMessage(e.target.value)} rows={4}
                  placeholder={t("jobs.applyMessagePlaceholder")}
                  data-testid="apply-message-input"
                  className="mt-1.5 w-full rounded-md border-2 border-slate-900 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
              </div>

              {user?.cv_path_name ? (
                <p className="flex items-center gap-2 rounded-md border border-green-600 bg-green-50 px-3 py-2 text-xs font-semibold text-green-900" data-testid="apply-cv-attached">
                  <CheckCircle2 className="h-4 w-4" strokeWidth={2} /> {t("jobs.cvFromProfile")} ({user.cv_path_name})
                </p>
              ) : (
                <p className="rounded-md border border-yellow-500 bg-yellow-50 px-3 py-2 text-xs font-semibold text-yellow-900" data-testid="apply-no-cv-warning">
                  {t("jobs.noCvWarning")}
                </p>
              )}
              <div className="flex gap-3">
                <button
                  type="button" onClick={() => setShowApply(false)}
                  data-testid="apply-cancel-button"
                  className="flex-1 rounded-full border-2 border-slate-900 bg-white py-2.5 text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5"
                >
                  {t("company.cancel")}
                </button>
                <button
                  type="submit" disabled={sending}
                  data-testid="apply-submit-button"
                  className="flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 py-2.5 text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 disabled:opacity-60"
                >
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />} {t("jobs.submitApplication")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
